/* ─── Trigger Container ─── */

.trigger {
  display: inline-flex;
  align-items: center;
  width: 100%;
  height: var(--nexus-control-md);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-control);
  background: var(--nexus-surface);
  color: var(--nexus-text);
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-sm);
  transition: border-color var(--nexus-transition-fast);
}

.trigger:hover:not(.disabled) {
  border-color: var(--nexus-border-hover, var(--nexus-primary));
}

.triggerOpen {
  border-color: var(--nexus-border-focus, var(--nexus-primary));
  box-shadow: 0 0 0 1px var(--nexus-border-focus, var(--nexus-primary));
}

.error {
  border-color: var(--nexus-danger);
}

.error:hover,
.error.triggerOpen {
  border-color: var(--nexus-danger);
  box-shadow: 0 0 0 1px var(--nexus-danger);
}

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.triggerInput {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 var(--nexus-space-lg);
  border: none;
  background: transparent;
  color: var(--nexus-text);
  font-family: inherit;
  font-size: inherit;
  outline: none;
}

.triggerInput::placeholder {
  color: var(--nexus-text-muted);
}

.triggerInput:disabled {
  cursor: not-allowed;
}

.triggerIconBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 100%;
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: var(--nexus-text-muted);
  cursor: pointer;
  border-radius: 0 var(--nexus-radius-control) var(--nexus-radius-control) 0;
  transition: color var(--nexus-transition-fast);
}

.triggerIconBtn:hover:not(:disabled) {
  color: var(--nexus-text);
}

.triggerIconBtn:disabled {
  cursor: not-allowed;
}

/* ─── Portal Menu ─── */

.menu {
  position: fixed;
  z-index: 9999;
  min-width: 280px;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 16px);
  background: var(--nexus-bg);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-surface);
  box-shadow: var(--nexus-shadow-dropdown, 0 8px 32px rgba(0, 0, 0, 0.4));
  overflow-x: hidden;
  overflow-y: auto;
  animation: dtpFadeIn 120ms ease-out;
}

@keyframes dtpFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ─── Calendar ─── */

.calendar {
  padding: var(--nexus-space-md);
}

.calHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--nexus-space-sm);
}

.monthLabel {
  font-size: var(--nexus-text-sm);
  font-weight: 600;
  color: var(--nexus-text);
}

.monthLabelBtn {
  font-size: var(--nexus-text-sm);
  font-weight: 600;
  color: var(--nexus-text);
  background: transparent;
  border: none;
  border-radius: var(--nexus-radius-sm);
  padding: var(--nexus-space-xs) var(--nexus-space-sm);
  cursor: pointer;
  transition: all var(--nexus-transition-fast);
  font-family: var(--nexus-font-sans);
}

.monthLabelBtn:hover {
  background: var(--nexus-surface-hover);
  color: var(--nexus-primary);
}

.navBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--nexus-radius-sm);
  background: transparent;
  color: var(--nexus-text-muted);
  cursor: pointer;
  transition: all var(--nexus-transition-fast);
}

.navBtn:hover {
  background: var(--nexus-surface-hover);
  color: var(--nexus-text);
}

/* Day name headers */

.dayNames {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: var(--nexus-space-xs);
}

.dayName {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--nexus-text-muted);
  padding: var(--nexus-space-xs) 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Day grid */

.dayGrid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.dayBtn {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--nexus-radius-sm);
  background: transparent;
  color: var(--nexus-text-secondary, var(--nexus-text));
  font-size: var(--nexus-text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--nexus-transition-fast);
}

.dayBtn:hover {
  background: var(--nexus-surface-hover);
  color: var(--nexus-text);
}

.dayOutside {
  color: var(--nexus-text-muted);
  opacity: 0.4;
}

.dayOutside:hover {
  opacity: 0.7;
}

.dayToday {
  color: var(--nexus-primary);
  font-weight: 700;
}

.daySelected {
  background: var(--nexus-primary);
  color: #fff;
  font-weight: 700;
  opacity: 1;
}

.daySelected:hover {
  background: var(--nexus-primary);
  color: #fff;
}

.dayDisabled {
  opacity: 0.25;
  cursor: not-allowed;
  pointer-events: none;
}

/* ─── Month / Year Picker Grid ─── */

.pickerGrid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--nexus-space-xs);
  padding-top: var(--nexus-space-xs);
}

.pickerCell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--nexus-space-sm) var(--nexus-space-xs);
  border: none;
  border-radius: var(--nexus-radius-sm);
  background: transparent;
  color: var(--nexus-text-secondary, var(--nexus-text));
  font-size: var(--nexus-text-sm);
  font-weight: 500;
  font-family: var(--nexus-font-sans);
  cursor: pointer;
  transition: all var(--nexus-transition-fast);
}

.pickerCell:hover {
  background: var(--nexus-surface-hover);
  color: var(--nexus-text);
}

.pickerCellCurrent {
  color: var(--nexus-primary);
  font-weight: 700;
}

.pickerCellSelected {
  background: var(--nexus-primary);
  color: #fff;
  font-weight: 700;
}

.pickerCellSelected:hover {
  background: var(--nexus-primary);
  color: #fff;
}

.pickerCellDisabled {
  opacity: 0.25;
  cursor: not-allowed;
  pointer-events: none;
}

/* ─── Time Picker ─── */

.timePicker {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  padding: var(--nexus-space-sm) var(--nexus-space-md);
  border-top: 1px solid var(--nexus-border);
}

.timeIcon {
  color: var(--nexus-text-muted);
  flex-shrink: 0;
}

.timeSelect {
  appearance: none;
  width: 48px;
  padding: var(--nexus-space-xs) var(--nexus-space-xs);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-sm);
  background: var(--nexus-surface);
  color: var(--nexus-text);
  font-size: var(--nexus-text-sm);
  font-family: var(--nexus-font-sans);
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: border-color var(--nexus-transition-fast);
}

.timeSelect:focus {
  outline: none;
  border-color: var(--nexus-primary);
}

.timeSep {
  font-weight: 700;
  color: var(--nexus-text-muted);
}

.ampmToggle {
  display: flex;
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-sm);
  overflow: hidden;
  margin-left: auto;
}

.ampmBtn {
  padding: var(--nexus-space-xs) var(--nexus-space-sm);
  border: none;
  background: transparent;
  color: var(--nexus-text-muted);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all var(--nexus-transition-fast);
}

.ampmBtn:hover {
  color: var(--nexus-text);
}

.ampmActive {
  background: var(--nexus-primary);
  color: #fff;
}

.ampmActive:hover {
  color: #fff;
}

/* ─── Footer ─── */

.menuFooter {
  display: flex;
  justify-content: flex-end;
  padding: var(--nexus-space-xs) var(--nexus-space-md) var(--nexus-space-sm);
  border-top: 1px solid var(--nexus-border);
}

.doneBtn {
  padding: var(--nexus-space-xs) var(--nexus-space-lg);
  border: none;
  border-radius: var(--nexus-radius-sm);
  background: var(--nexus-primary);
  color: #fff;
  font-size: var(--nexus-text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--nexus-transition-fast);
}

.doneBtn:hover {
  opacity: 0.9;
}

/* ─── Mobile ─── */

@media (max-width: 768px) {
  /* Let JS clamp left/top to the viewport; only relax the min-width so the
     menu can shrink on very narrow screens. */
  .menu {
    min-width: auto;
  }
}
